import { useEffect, useState } from "react";
import api, { API_BASE } from "./api";

// Media value can be a plain string (image URL) or an object:
// { image, image_mobile, video, video_mobile, overlay, video_start, video_end }
export const normalizeMedia = (v) => {
  if (!v) return { image: "", image_mobile: "", video: "", video_mobile: "", overlay: 0, video_start: 0, video_end: 0 };
  if (typeof v === "string") return { image: v, image_mobile: "", video: "", video_mobile: "", overlay: 0, video_start: 0, video_end: 0 };
  return {
    image: v.image || "",
    image_mobile: v.image_mobile || "",
    video: v.video || "",
    video_mobile: v.video_mobile || "",
    overlay: Number(v.overlay) || 0,
    video_start: Number(v.video_start) || 0,
    video_end: Number(v.video_end) || 0,
  };
};

// Collapse back to a plain string when only the desktop image is set (backward compatible)
export const packMedia = (m) => {
  const n = normalizeMedia(m);
  if (!n.image_mobile && !n.video && !n.video_mobile && !n.overlay && !n.video_start && !n.video_end) return n.image;
  return n;
};

export const mediaImage = (v) => (typeof v === "string" ? v : v?.image || "");

export const hasMedia = (v) => {
  const m = normalizeMedia(v);
  return !!(m.image || m.video);
};

// Server-side optimized (resized + webp) version of an image for mobile
export const optimizedImg = (src, w = 828, q) =>
  `${API_BASE}/img?src=${encodeURIComponent(src)}&w=${w}${q ? `&q=${q}` : ""}`;

// Parses "1:23", "83", "1m23s", "83s" → integer seconds.
export const parseTime = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Math.max(0, Math.floor(v));
  const s = String(v).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const mmss = s.match(/^(\d+):([0-5]?\d)$/);
  if (mmss) return parseInt(mmss[1], 10) * 60 + parseInt(mmss[2], 10);
  const ms = s.match(/^(?:(\d+)m)?(?:(\d+)s?)?$/);
  if (ms && (ms[1] || ms[2])) return (parseInt(ms[1] || 0, 10) * 60) + parseInt(ms[2] || 0, 10);
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

// Returns a background-style embed URL for YouTube/Vimeo links, or null for direct files.
// opts: { start, end } — seconds; used to loop only a segment of the video.
export const videoEmbed = (url, opts = {}) => {
  if (!url) return null;
  const start = parseTime(opts.start);
  const end = parseTime(opts.end);
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) {
    const params = [
      "autoplay=1", "mute=1", "loop=1", `playlist=${yt[1]}`,
      "controls=0", "rel=0", "playsinline=1", "modestbranding=1",
      "iv_load_policy=3", "disablekb=1",
      "cc_load_policy=0", "cc_lang_pref=none", "hl=en",
      "enablejsapi=1",
    ];
    if (start > 0) params.push(`start=${start}`);
    if (end > 0 && end > start) params.push(`end=${end}`);
    return `https://www.youtube.com/embed/${yt[1]}?${params.join("&")}`;
  }
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) {
    const frag = start > 0 ? `#t=${start}s` : "";
    return `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1&loop=1&background=1${frag}`;
  }
  return null;
};

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return isMobile;
};

const CHUNK_SIZE = 4 * 1024 * 1024;

// Uploads a file; large files are sent in chunks to bypass proxy limits
export async function uploadMediaFile(file, onProgress) {
  if (file.size <= CHUNK_SIZE) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onProgress?.(100);
    return data.url;
  }
  const uploadId = (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  ).replace(/[^a-zA-Z0-9-]/g, "");
  const total = Math.ceil(file.size / CHUNK_SIZE);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", String(i));
    fd.append("file", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), `${uploadId}.${i}`);
    await api.post("/admin/upload-chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onProgress?.(Math.round(((i + 1) / total) * 95));
  }
  const { data } = await api.post("/admin/upload-complete", {
    upload_id: uploadId,
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    total_chunks: total,
  });
  onProgress?.(100);
  return data.url;
}
